import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PricingModal from './PricingModal';
import { 
  ArrowRightOnRectangleIcon, 
  ShieldCheckIcon,
  SparklesIcon,
  CpuChipIcon,
  ScaleIcon,
  BriefcaseIcon
} from '@heroicons/react/24/outline';

function Navbar() {
  const { user, userProfile, logout, switchRole } = useAuth();
  const [showPricing, setShowPricing] = useState(false);

  const handleLogout = async () => {
    await logout();
  };

  const roles = [
    { id: 'Executive', label: 'CRO / Executive', icon: BriefcaseIcon, color: 'purple' },
    { id: 'DPO', label: 'DPO / Legal', icon: ScaleIcon, color: 'blue' },
    { id: 'Developer', label: 'ML Engineer', icon: CpuChipIcon, color: 'emerald' },
  ];

  return (
    <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-xs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo & Node Telemetry */}
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-md shadow-blue-500/20">
                <ShieldCheckIcon className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-base sm:text-lg font-black text-slate-900 tracking-tight">
                    OpsSight AI
                  </span>
                  <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">
                    Arbiter Core
                  </span>
                </div>
                <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-slate-500 font-medium">
                  <span>EU AI Act &bull; Colorado SB 205 &bull; NIST SP 1270-1</span>
                </div>
              </div>
            </div>

            {/* Cloud Run Live Node Pill */}
            <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-[11px] text-slate-600">
              <span className="flex h-1.5 w-1.5 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
              </span>
              <span className="font-semibold text-slate-700">Cloud Run:</span>
              <span className="font-mono text-slate-500 text-[10px]">us-central1</span>
            </div>
          </div>

          {/* Interactive Role Switcher Pill Selector */}
          <div className="flex items-center gap-1 sm:gap-2">
            <div className="hidden md:flex items-center bg-slate-100/80 p-1 rounded-xl border border-slate-200 text-xs">
              <span className="text-[11px] font-semibold text-slate-400 px-2 flex items-center gap-1">
                <SparklesIcon className="w-3.5 h-3.5 text-blue-500" />
                Role:
              </span>
              {roles.map((r) => {
                const isActive = userProfile?.role === r.id;
                const Icon = r.icon;
                return (
                  <button
                    key={r.id}
                    onClick={() => switchRole(r.id)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                      isActive
                        ? r.color === 'purple'
                          ? 'bg-purple-600 text-white shadow-xs'
                          : r.color === 'blue'
                          ? 'bg-blue-600 text-white shadow-xs'
                          : 'bg-emerald-600 text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{r.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Enterprise Plan & Fine Protection Trigger */}
            <button
              onClick={() => setShowPricing(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white text-xs font-bold shadow-xs transition-all transform hover:-translate-y-0.5"
              title="View commercial tiers & statutory fine protection ROI"
            >
              <SparklesIcon className="w-3.5 h-3.5 animate-soft-pulse text-yellow-300" />
              <span className="hidden sm:inline">Enterprise</span> Licensing
            </button>

            {/* User Profile Info & Sign Out */}
            <div className="flex items-center pl-2 sm:pl-3 border-l border-slate-200 space-x-3">
              <div className="flex items-center space-x-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shadow-xs ${
                  userProfile?.role === 'Executive' 
                    ? 'bg-purple-100 text-purple-800 border border-purple-200' 
                    : userProfile?.role === 'DPO'
                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                    : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                }`}>
                  {userProfile?.name ? userProfile.name.split(' ').map(n => n[0]).join('') : 'U'}
                </div>
                <div className="hidden xl:block text-left">
                  <div className="text-xs font-bold text-slate-800 leading-tight">
                    {userProfile?.name || user?.email}
                  </div>
                  <div className="text-[10px] text-slate-500 capitalize">
                    {userProfile?.role || 'Auditor'}
                  </div>
                </div>
              </div>

              <button
                onClick={handleLogout}
                title="Sign out of governance console"
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Enterprise Pricing & Statutory Fine Exposure Modal */}
      <PricingModal isOpen={showPricing} onClose={() => setShowPricing(false)} />
    </nav>
  );
}

export default Navbar;